import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, FileText, DollarSign } from "lucide-react";
import { Format, Section, MAX_TOTAL_PAGES } from "@/data/agendaData";

interface OrderSummaryProps {
  selectedFormat?: Format;
  selectedSections: Section[];
  customizationPrice?: number;
  onAddToCart?: () => void;
}

export const OrderSummary = ({
  selectedFormat,
  selectedSections,
  customizationPrice = 0,
  onAddToCart,
}: OrderSummaryProps) => {
  const formatPrice = selectedFormat?.basePrice || 0;
  const sectionsPrice = selectedSections.reduce((sum, section) => sum + section.price, 0);
  const totalPrice = formatPrice + sectionsPrice + customizationPrice;

  // Calcular páginas de secciones
  const sectionPages = selectedSections.reduce((sum, section) => sum + section.pages, 0);
  const pagesRemaining = MAX_TOTAL_PAGES - sectionPages;

  // Validación: formato seleccionado y no exceder límite de páginas
  const isValid = selectedFormat && sectionPages > 0 && sectionPages <= MAX_TOTAL_PAGES;
  const selectedCount = selectedSections.length;

  return (
    <Card className="sticky top-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Resumen de tu Agenda
        </CardTitle>
        <CardDescription>
          Revisa tu configuración antes de continuar
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Formato seleccionado */}
        <div>
          <h4 className="font-semibold text-sm mb-2">Formato</h4>
          {selectedFormat ? (
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <p className="text-sm font-medium">{selectedFormat.name}</p>
                <p className="text-xs text-muted-foreground">
                  {selectedFormat.totalPages} páginas
                </p>
              </div>
              <p className="text-sm font-bold">${formatPrice.toFixed(2)}</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              No has seleccionado un formato
            </p>
          )}
        </div>

        <Separator />

        {/* Secciones seleccionadas */}
        <div>
          <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
            Secciones
            <Badge variant="secondary" className="text-xs">
              {selectedCount}
            </Badge>
          </h4>

          {selectedSections.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {selectedSections.map((section) => (
                <div key={section.id} className="flex justify-between items-start text-sm">
                  <div className="flex items-start gap-1.5 flex-1">
                    <span className="text-base">{section.icon}</span>
                    <div>
                      <p className="font-medium leading-tight">{section.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {section.pages} páginas
                      </p>
                    </div>
                  </div>
                  <p className="font-medium text-primary">
                    +${section.price.toFixed(2)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              No has seleccionado secciones
            </p>
          )}
        </div>

        <Separator />

        {/* Desglose de precios */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Subtotal formato:</span>
            <span className="font-medium">${formatPrice.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              Subtotal secciones ({selectedCount}):
            </span>
            <span className="font-medium">${sectionsPrice.toFixed(2)}</span>
          </div>
          {customizationPrice > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Personalización:</span>
              <span className="font-medium text-primary">
                +${customizationPrice.toFixed(2)}
              </span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Páginas de secciones:</span>
            <span className="font-medium">{sectionPages} / {MAX_TOTAL_PAGES}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Páginas restantes:</span>
            <span className={`font-medium ${pagesRemaining < 50 ? 'text-orange-600' : 'text-green-600'}`}>
              {pagesRemaining}
            </span>
          </div>
        </div>

        <Separator />

        {/* Total */}
        <div className="bg-primary/5 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              <span className="font-bold text-lg">Total:</span>
            </div>
            <span className="font-bold text-2xl text-primary">
              ${totalPrice.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Validaciones */}
        {!isValid && (
          <div className="text-xs text-muted-foreground space-y-1">
            {!selectedFormat && (
              <p className="text-orange-600 dark:text-orange-400">
                • Selecciona un formato
              </p>
            )}
            {sectionPages === 0 && (
              <p className="text-orange-600 dark:text-orange-400">
                • Selecciona al menos una sección
              </p>
            )}
            {sectionPages > MAX_TOTAL_PAGES && (
              <p className="text-orange-600 dark:text-orange-400">
                • Has excedido el límite de {MAX_TOTAL_PAGES} páginas
              </p>
            )}
          </div>
        )}

        {/* Botón de acción */}
        <Button
          className="w-full relative overflow-hidden group"
          size="lg"
          disabled={!isValid}
          onClick={onAddToCart}
        >
          <span className="relative z-10 flex items-center justify-center">
            <ShoppingCart className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
            Agregar al Carrito
          </span>
          {isValid && (
            <span className="absolute inset-0 bg-primary-dark scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
          )}
        </Button>

        {isValid && (
          <p className="text-xs text-center text-muted-foreground animate-pulse">
            Serás redirigido a tu carrito para completar la compra
          </p>
        )}
      </CardContent>
    </Card>
  );
};
